import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { getAppInsights } from './Global';

// Log levels
export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

// Log types for categorization
export enum LogType {
  General = 'General',
  Performance = 'Performance',
  UserAction = 'UserAction',
  API = 'API',
  Component = 'Component',
  Navigation = 'Navigation',
  Authentication = 'Authentication',
  Business = 'Business',
  Debug = 'Debug'
}

// Log context interface
export interface LogContext {
  userId?: string;
  sessionId?: string;
  userAgent?: string;
  url?: string;
  pathname?: string;
  timestamp?: string;
  component?: string;
  feature?: string;
  route?: string;
  method?: string;
  operationType?: string;
  customProperties?: Record<string, any>;
}

// Log entry interface
export interface LogEntry {
  logId: string;
  level: LogLevel;
  type: LogType;
  message: string;
  context: LogContext;
  data?: any;
  error?: Error;
}

// Rate limiting interface
interface LogRateLimit {
  [key: string]: {
    count: number;
    lastLogged: number;
  };
}

class LoggingService {
  private appInsights: ApplicationInsights | undefined;
  private logRateLimit: LogRateLimit = {};
  private maxLogsPerMinute = 50; // Higher than errors since we log more frequently
  private isProduction = process.env.NODE_ENV === 'production';
  private isDevelopment = process.env.NODE_ENV === 'development';

  constructor() {
    this.appInsights = getAppInsights();
  }

  /**
   * Generate unique log ID with prefix
   */
  private generateLogId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
  }

  /**
   * Check if log should be logged based on rate limiting
   */
  private shouldLog(logKey: string, level: LogLevel): boolean {
    // Always allow ERROR level logs
    if (level === LogLevel.ERROR) return true;

    // In development, allow more verbose logging
    if (this.isDevelopment && (level === LogLevel.DEBUG || level === LogLevel.INFO)) {
      return true;
    }

    // In production, apply rate limiting for non-error logs
    if (this.isProduction) {
      const now = Date.now();
      const oneMinute = 60 * 1000;

      if (!this.logRateLimit[logKey]) {
        this.logRateLimit[logKey] = { count: 1, lastLogged: now };
        return true;
      }

      const logInfo = this.logRateLimit[logKey];
      
      // Reset count if more than a minute has passed
      if (now - logInfo.lastLogged > oneMinute) {
        logInfo.count = 1;
        logInfo.lastLogged = now;
        return true;
      }

      // Check if we've exceeded the limit
      if (logInfo.count >= this.maxLogsPerMinute) {
        return false;
      }

      logInfo.count++;
    }

    return true;
  }

  /**
   * Get current log context
   */
  private getLogContext(): LogContext {
    return {
      userId: 'unknown', // Could be enhanced with actual user ID
      sessionId: this.getSessionId(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      pathname: window.location.pathname,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Get or create session ID
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('loggingSessionId');
    if (!sessionId) {
      sessionId = this.generateLogId('SES');
      sessionStorage.setItem('loggingSessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Main logging method
   */
  private log(logEntry: LogEntry): void {
    const logKey = `${logEntry.level}-${logEntry.type}-${logEntry.message.substring(0, 50)}`;
    
    // Check rate limiting
    if (!this.shouldLog(logKey, logEntry.level)) {
      return;
    }

    // Merge provided context with current context
    const fullContext: LogContext = {
      ...this.getLogContext(),
      ...logEntry.context
    };

    // Log to console based on environment and level
    this.logToConsole(logEntry, fullContext);

    // Log to Application Insights for important logs
    if (this.shouldLogToApplicationInsights(logEntry.level)) {
      this.logToApplicationInsights(logEntry, fullContext);
    }
  }

  /**
   * Determine if log should go to Application Insights
   */
  private shouldLogToApplicationInsights(level: LogLevel): boolean {
    if (this.isDevelopment) {
      // In development, only log WARN and ERROR to Application Insights
      return level === LogLevel.WARN || level === LogLevel.ERROR;
    }
    
    // In production, log INFO, WARN, and ERROR to Application Insights
    return level !== LogLevel.DEBUG;
  }

  /**
   * Log to console with appropriate styling and grouping
   */
  private logToConsole(logEntry: LogEntry, context: LogContext): void {
    if (this.isProduction && logEntry.level === LogLevel.DEBUG) {
      return; // Don't log DEBUG in production console
    }

    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const prefix = `[${timestamp}] [${logEntry.level}] [${logEntry.type}]`;

    switch (logEntry.level) {
      case LogLevel.DEBUG:
        if (logEntry.data) {
          console.group(`🔍 ${prefix} ${logEntry.message}`);
          console.log('Data:', logEntry.data);
          if (context.component) console.log('Component:', context.component);
          if (context.method) console.log('Method:', context.method);
          console.groupEnd();
        } else {
          console.debug(`🔍 ${prefix} ${logEntry.message}`);
        }
        break;

      case LogLevel.INFO:
        if (logEntry.data) {
          console.group(`ℹ️ ${prefix} ${logEntry.message}`);
          console.log('Data:', logEntry.data);
          if (context.component) console.log('Component:', context.component);
          console.groupEnd();
        } else {
          console.info(`ℹ️ ${prefix} ${logEntry.message}`);
        }
        break;

      case LogLevel.WARN:
        if (logEntry.data || logEntry.error) {
          console.group(`⚠️ ${prefix} ${logEntry.message}`);
          if (logEntry.data) console.log('Data:', logEntry.data);
          if (logEntry.error) console.warn('Error:', logEntry.error);
          if (context.component) console.log('Component:', context.component);
          console.groupEnd();
        } else {
          console.warn(`⚠️ ${prefix} ${logEntry.message}`);
        }
        break;

      case LogLevel.ERROR:
        console.group(`🚨 ${prefix} ${logEntry.message}`);
        if (logEntry.error) console.error('Error:', logEntry.error);
        if (logEntry.data) console.log('Data:', logEntry.data);
        console.log('Context:', context);
        console.groupEnd();
        break;
    }
  }

  /**
   * Log to Application Insights
   */
  private logToApplicationInsights(logEntry: LogEntry, context: LogContext): void {
    try {
      if (!this.appInsights) {
        return;
      }

      // Prepare properties for Application Insights
      const properties: Record<string, string> = {
        logId: logEntry.logId,
        level: logEntry.level,
        type: logEntry.type,
        message: logEntry.message,
        userId: context.userId || 'unknown',
        sessionId: context.sessionId || 'unknown',
        userAgent: context.userAgent || 'unknown',
        url: context.url || 'unknown',
        pathname: context.pathname || 'unknown',
        timestamp: context.timestamp || new Date().toISOString()
      };

      // Add optional context properties
      if (context.component) properties.component = context.component;
      if (context.feature) properties.feature = context.feature;
      if (context.route) properties.route = context.route;
      if (context.method) properties.method = context.method;

      // Add custom properties
      if (context.customProperties) {
        Object.entries(context.customProperties).forEach(([key, value]) => {
          properties[`custom_${key}`] = String(value);
        });
      }

      // Add data if present
      if (logEntry.data) {
        properties.data = typeof logEntry.data === 'string' 
          ? logEntry.data 
          : JSON.stringify(logEntry.data);
      }

      // Track based on level
      if (logEntry.level === LogLevel.ERROR && logEntry.error) {
        // Track as exception for errors
        this.appInsights.trackException({
          exception: logEntry.error,
          properties: properties
        });
      } else {
        // Track as trace for other levels
        const severityLevel = this.getSeverityLevel(logEntry.level);
        this.appInsights.trackTrace({
          message: `[${logEntry.type}] ${logEntry.message}`,
          severityLevel,
          properties
        });
      }

      // Track custom event for easier querying
      this.appInsights.trackEvent({
        name: `Log:${logEntry.level}_${logEntry.type}`,
        properties: properties
      });

    } catch (loggingError) {
      console.error('Failed to log to Application Insights:', loggingError);
    }
  }

  /**
   * Convert LogLevel to Application Insights SeverityLevel
   */
  private getSeverityLevel(level: LogLevel): number {
    switch (level) {
      case LogLevel.DEBUG: return 0; // Verbose
      case LogLevel.INFO: return 1;  // Information
      case LogLevel.WARN: return 2;  // Warning
      case LogLevel.ERROR: return 3; // Error
      default: return 1;
    }
  }

  // Public logging methods

  /**
   * Log debug information (development only)
   */
  public debug(message: string, data?: any, context?: Partial<LogContext>): void {
    this.log({
      logId: this.generateLogId('DBG'),
      level: LogLevel.DEBUG,
      type: LogType.Debug,
      message,
      data,
      context: context || {}
    });
  }

  /**
   * Log general information
   */
  public info(message: string, data?: any, context?: Partial<LogContext>): void {
    this.log({
      logId: this.generateLogId('INF'),
      level: LogLevel.INFO,
      type: LogType.General,
      message,
      data,
      context: context || {}
    });
  }

  /**
   * Log warnings
   */
  public warn(message: string, data?: any, context?: Partial<LogContext>): void {
    this.log({
      logId: this.generateLogId('WRN'),
      level: LogLevel.WARN,
      type: LogType.General,
      message,
      data,
      context: context || {}
    });
  }

  /**
   * Log errors
   */
  public error(message: string, error?: Error, data?: any, context?: Partial<LogContext>): void {
    this.log({
      logId: this.generateLogId('ERR'),
      level: LogLevel.ERROR,
      type: LogType.General,
      message,
      error,
      data,
      context: context || {}
    });
  }

  // Specialized logging methods

  /**
   * Log performance metrics
   */
  public performance(message: string, duration: number, context?: Partial<LogContext>): void {
    this.log({
      logId: this.generateLogId('PRF'),
      level: LogLevel.INFO,
      type: LogType.Performance,
      message,
      data: { duration, unit: 'ms' },
      context: context || {}
    });
  }

  /**
   * Log user actions
   */
  public userAction(action: string, data?: any, context?: Partial<LogContext>): void {
    this.log({
      logId: this.generateLogId('USR'),
      level: LogLevel.INFO,
      type: LogType.UserAction,
      message: `User action: ${action}`,
      data,
      context: context || {}
    });
  }

  /**
   * Log API calls
   */
  public apiCall(method: string, url: string, status?: number, duration?: number, context?: Partial<LogContext>): void {
    const level = status && status >= 400 ? LogLevel.ERROR : LogLevel.INFO;
    this.log({
      logId: this.generateLogId('API'),
      level,
      type: LogType.API,
      message: `${method} ${url}`,
      data: { method, url, status, duration },
      context: context || {}
    });
  }

  /**
   * Log component lifecycle events
   */
  public component(componentName: string, event: string, data?: any, context?: Partial<LogContext>): void {
    this.log({
      logId: this.generateLogId('CMP'),
      level: LogLevel.DEBUG,
      type: LogType.Component,
      message: `${componentName}: ${event}`,
      data,
      context: {
        component: componentName,
        ...context
      }
    });
  }

  /**
   * Log navigation events
   */
  public navigation(from: string, to: string, context?: Partial<LogContext>): void {
    this.log({
      logId: this.generateLogId('NAV'),
      level: LogLevel.INFO,
      type: LogType.Navigation,
      message: `Navigation: ${from} → ${to}`,
      data: { from, to },
      context: context || {}
    });
  }

  /**
   * Log business logic events
   */
  public business(event: string, data?: any, context?: Partial<LogContext>): void {
    this.log({
      logId: this.generateLogId('BIZ'),
      level: LogLevel.INFO,
      type: LogType.Business,
      message: event,
      data,
      context: context || {}
    });
  }

  /**
   * Set maximum logs per minute for rate limiting
   */
  public setMaxLogsPerMinute(max: number): void {
    this.maxLogsPerMinute = max;
  }

  /**
   * Clear rate limiting cache
   */
  public clearRateLimit(): void {
    this.logRateLimit = {};
  }
}

// Create singleton instance
const loggingService = new LoggingService();

export default loggingService; 